import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { Waiting } from '../../../components/Waiting/Waiting';

export const StoreDetailsSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.STORE)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.STORE, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Mağaza Bilgileri
      </Typography>
      <Divider />

      <SettingsRow
        name='Mağaza Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_name')}
        onChange={(newValue) => handleChange('store_name', newValue)}
      />
      <SettingsRow
        name='Mağaza Açıklaması'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_tagline')}
        onChange={(newValue) => handleChange('store_tagline', newValue)}
      />
      <SettingsRow
        name='Mağaza E-Posta Adresi'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_email')}
        onChange={(newValue) => handleChange('store_email', newValue)}
      />
      <SettingsRow
        name='Mağaza Telefonu'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_phone')}
        onChange={(newValue) => handleChange('store_phone', newValue)}
      />
      <SettingsRow
        name='Mağaza Adresi 1. Satır'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_address_1')}
        onChange={(newValue) => handleChange('store_address_1', newValue)}
        multiline
      />
      <SettingsRow
        name='Mağaza Adresi 2. Satır'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_address_2')}
        onChange={(newValue) => handleChange('store_address_2', newValue)}
        multiline
      />
      <SettingsRow
        name='Mağaza Şehir'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_city')}
        onChange={(newValue) => handleChange('store_city', newValue)}
      />
      <SettingsRow
        name='Mağaza Ülke'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_country')}
        onChange={(newValue) => handleChange('store_country', newValue)}
      />
      <SettingsRow
        name='Mağaza Posta Kodu'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'store_zip')}
        onChange={(newValue) => handleChange('store_zip', newValue)}
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
